import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Copy, Download, BadgeCheck, Lock, Loader2, Link2, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

async function download(url, filename) {
  try {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(String(r.status));
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch { toast.error("No se pudo descargar la imagen"); }
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <Card className="p-5 sm:p-6" data-testid={testId}>
      <h3 className="font-bold flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-primary" />{title}</h3>
      {children}
    </Card>
  );
}

function BioLink({ kit }) {
  const copy = () => navigator.clipboard.writeText(kit.profile_url).then(() => toast.success("Enlace copiado"));
  return (
    <Section icon={Link2} title="Enlace «Reserva tu Cupo» para tu Bio" testId="igkit-bio">
      <p className="text-sm text-muted-foreground mb-3">Este enlace lleva a tus seguidores directo a tus talleres en VIVOTaller, donde reservan y pagan de forma segura.</p>
      <div className="flex gap-2">
        <Input readOnly value={kit.profile_url} onFocus={(e) => e.target.select()} data-testid="igkit-profile-url" />
        <Button onClick={copy} className="shrink-0" data-testid="igkit-copy-link"><Copy className="w-4 h-4 mr-1" />Copiar</Button>
      </div>
      <ol className="mt-4 space-y-1.5 text-sm list-decimal pl-5" data-testid="igkit-bio-steps">
        <li>En Instagram, toca <strong>Editar perfil → Enlaces → Agregar enlace externo</strong>.</li>
        <li>Pega el enlace y ponle como título <strong>«Reserva tu Cupo»</strong>.</li>
        <li>¿Usas Linktree o Beacons? Agrégalo como un botón más con ese mismo título.</li>
      </ol>
    </Section>
  );
}

function VerifiedBadge({ kit }) {
  const url = `${BACKEND}/api/ig-kit/badge/${kit.business_id}.png`;
  return (
    <Section icon={BadgeCheck} title="Sello «Maestro Verificado VIVOTaller»" testId="igkit-badge">
      {kit.verified ? (
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <img src={url} alt="Sello Maestro Verificado" className="w-40 h-40 shrink-0" data-testid="igkit-badge-img"
            onError={() => toast.error("No se pudo generar el sello. Intenta de nuevo en unos minutos.")} />
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Úsalo como portada de tus <strong>Historias destacadas</strong> o en tus publicaciones. Transmite a tus seguidores la confianza de reservar en una plataforma segura.</p>
            <Button onClick={() => download(url, "maestro-verificado-vivotaller.png")} data-testid="igkit-badge-download"><Download className="w-4 h-4 mr-1" />Descargar PNG</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-xl border border-dashed p-4" data-testid="igkit-badge-locked">
          <Lock className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="text-sm"><p className="font-semibold">Aún no disponible</p><p className="text-muted-foreground">{kit.reason}</p></div>
        </div>
      )}
    </Section>
  );
}

function StoryTemplate({ kit }) {
  const [wid, setWid] = useState(kit.workshops[0]?.id || "");
  const [loading, setLoading] = useState(true);
  const url = wid ? `${BACKEND}/api/ig-kit/story/${wid}.jpg` : null;
  useEffect(() => { setLoading(true); }, [wid]);
  return (
    <Section icon={Instagram} title="Plantilla para Instagram Stories" testId="igkit-story">
      {!kit.workshops.length ? <p className="text-sm text-muted-foreground" data-testid="igkit-story-empty">Publica un taller para generar su plantilla de historia.</p> : (
        <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-start">
          <div className="space-y-3 min-w-0">
            <p className="text-sm text-muted-foreground">Imagen vertical 1080×1920 con la foto, nombre y próxima fecha de tu taller. Súbela a tu historia, agrega el sticker de enlace y etiqueta a <strong>{kit.account}</strong>.</p>
            <Select value={wid} onValueChange={setWid}>
              <SelectTrigger data-testid="igkit-story-select"><SelectValue /></SelectTrigger>
              <SelectContent>{kit.workshops.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => download(url, "historia-vivotaller.jpg")} data-testid="igkit-story-download"><Download className="w-4 h-4 mr-1" />Descargar historia</Button>
          </div>
          <div className="relative w-[150px] h-[267px] mx-auto rounded-xl overflow-hidden border bg-muted">
            {loading && <Loader2 className="absolute inset-0 m-auto w-5 h-5 animate-spin text-primary" />}
            <img key={url} src={url} alt="Vista previa de la historia" onLoad={() => setLoading(false)}
              onError={() => { setLoading(false); toast.error("No se pudo generar la plantilla de historia."); }} className="w-full h-full object-cover" data-testid="igkit-story-preview" />
          </div>
        </div>
      )}
    </Section>
  );
}

const TIPS = (acc) => [
  ["Publicaciones en colaboración", `Al publicar el cartel o video de tu próximo taller, toca «Etiquetar personas → Invitar colaborador» y agrega a ${acc}. La publicación aparece en ambos perfiles y llega a más personas.`],
  ["Etiqueta en tus historias", `Menciona a ${acc} en tus historias: así podemos compartirlas y mostrarte a nuestra comunidad.`],
  ["Palabra clave en comentarios", "Escribe en tu publicación «Comenta TALLER y te envío el link de inscripción por interno». Responde con tu enlace de VIVOTaller por mensaje directo: tu seguidor reserva sin salir a buscarlo."],
  ["Historias destacadas", "Crea una destacada llamada «Talleres» con el sello de Maestro Verificado como portada y guarda ahí tus plantillas de historias."],
  ["Maestro de la Semana", `Cada semana VIVOTaller destaca a un Maestro en su portada e Instagram. Mantén tus talleres al día y sin denuncias para poder ser elegido.`],
];

function Guide({ kit }) {
  return (
    <Section icon={Lightbulb} title="Guía de buenas prácticas" testId="igkit-guide">
      <ul className="space-y-3">
        {TIPS(kit.account).map(([t, d], i) => (
          <li key={t} className="flex gap-3" data-testid={`igkit-tip-${i}`}>
            <span className="grid place-items-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">{i + 1}</span>
            <div className="text-sm"><p className="font-semibold">{t}</p><p className="text-muted-foreground">{d}</p></div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function InstagramKit() {
  const [kit, setKit] = useState(null);
  useEffect(() => { api.get("/maestro/ig-kit").then((r) => setKit(r.data)).catch(() => setKit(false)); }, []);
  if (kit === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!kit) return <p className="pt-6 text-sm text-muted-foreground">No se pudo cargar tu kit de Instagram.</p>;
  return (
    <div className="pt-6 grid lg:grid-cols-2 gap-6" data-testid="instagram-kit">
      <BioLink kit={kit} />
      <VerifiedBadge kit={kit} />
      <StoryTemplate kit={kit} />
      <Guide kit={kit} />
    </div>
  );
}
